import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ComposedChart,
  Area,
} from "recharts";
import {
  FileDown,
  Filter,
  ArrowLeft,
  ChevronRight,
  AlertTriangle,
  Info,
  Download,
  Scale,
  TrendingUp,
  Calculator,
} from "lucide-react";
import "./reconciliation-styles.css";
import MaritimeLayout from "../common/Applayout";

export default function MonthlyReconciliationDashboard() {
  // ▼▼ State for drilldowns & filters
  const [selectedEmirate, setSelectedEmirate] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState("Nov 2025");

  // ▼▼ Emirate-level summary (Expected vs Paid vs Variance)
  const emirateRecon = [
    {
      emirate: "Dubai",
      expected: 483_750_000,
      paid: 482_900_000,
      variance: -850_000,
      matchRate: 99.82,
    },
    {
      emirate: "Abu Dhabi",
      expected: 153_375_000,
      paid: 152_800_000,
      variance: -575_000,
      matchRate: 99.63,
    },
    {
      emirate: "Sharjah",
      expected: 52_000_000,
      paid: 52_000_000,
      variance: 0,
      matchRate: 100.0,
    },
    {
      emirate: "Fujairah",
      expected: 20_000_000,
      paid: 19_500_000,
      variance: -500_000,
      matchRate: 97.5,
    },
    {
      emirate: "RAK",
      expected: 13_500_000,
      paid: 13_550_000,
      variance: 50_000,
      matchRate: 100.37,
    },
    {
      emirate: "Ajman",
      expected: 9_000_000,
      paid: 9_000_000,
      variance: 0,
      matchRate: 100.0,
    },
    {
      emirate: "UAQ",
      expected: 8_500_000,
      paid: 8_500_000,
      variance: 0,
      matchRate: 100.0,
    },
  ];

  // ▼▼ Port-level details per Emirate
  const portsByEmirate = {
    Dubai: [
      {
        port: "Jebel Ali",
        expected: 483_750_000,
        paid: 482_900_000,
        variance: -850_000,
        status: "MATCHED",
      },
    ],
    "Abu Dhabi": [
      {
        port: "Khalifa",
        expected: 153_375_000,
        paid: 152_800_000,
        variance: -575_000,
        status: "PARTIAL",
      },
    ],
    Sharjah: [
      {
        port: "Sharjah KCT",
        expected: 52_000_000,
        paid: 52_000_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
    Fujairah: [
      {
        port: "Fujairah Port",
        expected: 20_000_000,
        paid: 19_500_000,
        variance: -500_000,
        status: "SHORT",
      },
    ],
    RAK: [
      {
        port: "RAK Ports",
        expected: 13_500_000,
        paid: 13_550_000,
        variance: 50_000,
        status: "EXCESS",
      },
    ],
    Ajman: [
      {
        port: "Ajman Port",
        expected: 9_000_000,
        paid: 9_000_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
    UAQ: [
      {
        port: "UAQ Port",
        expected: 8_500_000,
        paid: 8_500_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
  };

  // ▼▼ Service-level details per Port
  const servicesByPort = {
    "Jebel Ali": [
      {
        service: "TEU Handling",
        expected: 400_000_000,
        paid: 399_500_000,
        variance: -500_000,
        status: "MATCHED",
      },
      {
        service: "Pilotage",
        expected: 83_750_000,
        paid: 83_400_000,
        variance: -350_000,
        status: "MATCHED",
      },
    ],
    Khalifa: [
      {
        service: "Container Ops",
        expected: 153_375_000,
        paid: 152_800_000,
        variance: -575_000,
        status: "PARTIAL",
      },
    ],
    "Sharjah KCT": [
      {
        service: "Bulk Cargo",
        expected: 52_000_000,
        paid: 52_000_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
    "Fujairah Port": [
      {
        service: "Marine Services",
        expected: 20_000_000,
        paid: 19_500_000,
        variance: -500_000,
        status: "SHORT",
      },
    ],
    "RAK Ports": [
      {
        service: "General Cargo",
        expected: 13_500_000,
        paid: 13_550_000,
        variance: 50_000,
        status: "EXCESS",
      },
    ],
    "Ajman Port": [
      {
        service: "Clearance",
        expected: 9_000_000,
        paid: 9_000_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
    "UAQ Port": [
      {
        service: "Clearance",
        expected: 8_500_000,
        paid: 8_500_000,
        variance: 0,
        status: "MATCHED",
      },
    ],
  };

  // ▼▼ AI Anomalies & notes
  const aiFindings = [
    {
      emirate: "Abu Dhabi",
      note: "Pattern of under-remittance in Container Ops (variance −0.37%). Recommend audit of PCS remittance feed.",
    },
    {
      emirate: "Fujairah",
      note: "Short payments detected in Marine Services (AED −0.5M). Likely delayed bank postings.",
    },
    {
      emirate: "RAK",
      note: "EXCESS +AED 50k appears to be duplicate settlement; mark for carry-forward adjustment.",
    },
  ];

  // ▼▼ Calculated national KPIs
  const kpis = useMemo(() => {
    const expected = emirateRecon.reduce((s, e) => s + e.expected, 0);
    const paid = emirateRecon.reduce((s, e) => s + e.paid, 0);
    const variance = paid - expected;
    const matchCount = emirateRecon.filter(
      (e) => Math.abs(e.variance) === 0
    ).length;
    const matchPct = (paid / expected) * 100;
    return { expected, paid, variance, matchPct, matchCount };
  }, [emirateRecon]);

  // ▼▼ Export handlers (stubbed)
  const exportData = (format) => {
    if (format === "csv") alert("Monthly Reconciliation data exported as CSV");
    else alert("Monthly Reconciliation report exported as PDF");
  };

  // ▼▼ Status helpers
  const getStatusClass = (status) => {
    switch (status) {
      case "MATCHED":
        return "status-matched";
      case "PARTIAL":
        return "status-partial";
      case "SHORT":
        return "status-short";
      case "EXCESS":
        return "status-excess";
      default:
        return "";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "MATCHED":
        return "matched";
      case "PARTIAL":
        return "partial";
      case "SHORT":
        return "short";
      case "EXCESS":
        return "excess";
      default:
        return "";
    }
  };

  const getVarianceClass = (variance) => {
    if (variance === 0) return "neutral-value";
    return variance > 0 ? "positive-value" : "negative-value";
  };

  // Custom Card Components
  const ReconCard = ({ children, className = "", hover = false, onClick }) => (
    <div
      className={`recon-card ${hover ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );

  const ReconCardHeader = ({ children, className = "" }) => (
    <div className={`recon-card-header ${className}`}>{children}</div>
  );

  const ReconCardTitle = ({ children, className = "" }) => (
    <h3 className={`recon-card-title ${className}`}>{children}</h3>
  );

  const ReconCardContent = ({ children, className = "" }) => (
    <div className={`recon-card-content ${className}`}>{children}</div>
  );

  // ▼▼ DRILLDOWN: Service-level view
  if (selectedPort) {
    const rows = (servicesByPort[selectedPort] || [])
      .filter((r) => !serviceFilter || r.service === serviceFilter)
      .filter((r) => !statusFilter || r.status === statusFilter);

    return (
      <div className="reconciliation-dashboard">
        <div className="drilldown-header">
          <div className="flex items-center">
            <button
              onClick={() => setSelectedPort(null)}
              className="recon-btn recon-btn-secondary"
            >
              <ArrowLeft className="mr-2" size={18} /> Back to Ports
            </button>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => exportData("csv")}
              className="recon-btn recon-btn-success"
            >
              <Download className="mr-2" size={16} /> Export CSV
            </button>
            <button
              onClick={() => exportData("pdf")}
              className="recon-btn recon-btn-primary"
            >
              <FileDown className="mr-2" size={16} /> Export PDF
            </button>
          </div>
        </div>

        <ReconCard>
          <ReconCardHeader>
            <ReconCardTitle>
              <div className="recon-icon">
                <Calculator size={20} />
              </div>
              Service-Level Reconciliation – {selectedPort}
            </ReconCardTitle>
          </ReconCardHeader>
          <ReconCardContent>
            <p className="text-slate-600 mb-4">
              Compare expected vs paid amounts per service under {selectedPort}.
              Use filters to isolate mismatches and generate audit-ready
              exports.
            </p>

            <div className="recon-filter-bar">
              <div className="recon-filter-item">
                <Filter className="mr-2" size={14} /> Service:{" "}
                {serviceFilter || "All"}
              </div>
              <div className="recon-filter-item">
                <Filter className="mr-2" size={14} /> Status:{" "}
                {statusFilter || "All"}
              </div>
              <div className="recon-filter-item">Date Range: {dateRange}</div>
            </div>
          </ReconCardContent>
        </ReconCard>

        <div className="chart-container">
          <h3 className="recon-card-title mb-4">
            Expected vs Paid by Service (AED)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={rows}>
              <XAxis dataKey="service" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Legend />
              <Bar
                dataKey="expected"
                name="Expected"
                fill="#94a3b8"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="paid"
                name="Paid"
                fill="#1d4ed8"
                radius={[2, 2, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="variance"
                name="Variance"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: "#ef4444", r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="data-grid data-grid-cols-3">
          {rows.map((r, i) => (
            <div key={i} className="service-card">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-800">{r.service}</h4>
                <span className={`status-badge ${getStatusBadge(r.status)}`}>
                  {r.status}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Expected:</span>
                  <span className="font-semibold">
                    AED {r.expected.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Paid:</span>
                  <span className="font-semibold text-blue-600">
                    AED {r.paid.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Variance:</span>
                  <span
                    className={`font-semibold ${getVarianceClass(r.variance)}`}
                  >
                    {r.variance > 0 ? "+" : ""}
                    {r.variance.toLocaleString()} AED
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ▼▼ DRILLDOWN: Port-level view
  if (selectedEmirate) {
    const rows = (portsByEmirate[selectedEmirate] || []).filter(
      (r) => !statusFilter || r.status === statusFilter
    );

    return (
      <div className="reconciliation-dashboard">
        <div className="drilldown-header">
          <div className="flex items-center">
            <button
              onClick={() => setSelectedEmirate(null)}
              className="recon-btn recon-btn-secondary"
            >
              <ArrowLeft className="mr-2" size={18} /> Back to Emirates
            </button>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => exportData("csv")}
              className="recon-btn recon-btn-success"
            >
              <Download className="mr-2" size={16} /> Export CSV
            </button>
            <button
              onClick={() => exportData("pdf")}
              className="recon-btn recon-btn-primary"
            >
              <FileDown className="mr-2" size={16} /> Export PDF
            </button>
          </div>
        </div>

        <ReconCard>
          <ReconCardHeader>
            <ReconCardTitle>
              <div className="recon-icon">
                <TrendingUp size={20} />
              </div>
              Port Reconciliation – {selectedEmirate}
            </ReconCardTitle>
          </ReconCardHeader>
          <ReconCardContent>
            <p className="text-slate-600 mb-4">
              This view compares expected vs paid amounts for each port in{" "}
              {selectedEmirate}. Click a port to drill down to service-level
              details.
            </p>

            <div className="recon-filter-bar">
              <div className="recon-filter-item">
                <Filter className="mr-2" size={14} /> Status:{" "}
                {statusFilter || "All"}
              </div>
              <div className="recon-filter-item">Date Range: {dateRange}</div>
            </div>
          </ReconCardContent>
        </ReconCard>

        <div className="chart-container">
          <h3 className="recon-card-title mb-4">
            Expected vs Paid by Port (AED)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={rows}>
              <XAxis dataKey="port" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Legend />
              <Bar
                dataKey="expected"
                name="Expected"
                fill="#94a3b8"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="paid"
                name="Paid"
                fill="#1d4ed8"
                radius={[2, 2, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="variance"
                name="Variance"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: "#ef4444", r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="data-grid data-grid-cols-3">
          {rows.map((r, i) => (
            <ReconCard
              key={i}
              hover={true}
              onClick={() => setSelectedPort(r.port)}
              className="emirate-card"
            >
              <ReconCardHeader>
                <ReconCardTitle>{r.port}</ReconCardTitle>
              </ReconCardHeader>
              <ReconCardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Expected:</span>
                    <span className="font-semibold">
                      AED {r.expected.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Paid:</span>
                    <span className="font-semibold text-blue-600">
                      AED {r.paid.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Variance:</span>
                    <span
                      className={`font-semibold ${getVarianceClass(
                        r.variance
                      )}`}
                    >
                      {r.variance > 0 ? "+" : ""}
                      {r.variance.toLocaleString()} AED
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Status:</span>
                    <span
                      className={`status-badge ${getStatusBadge(r.status)}`}
                    >
                      {r.status}
                    </span>
                  </div>
                  <div className="flex items-center text-blue-600 font-semibold mt-2">
                    View Services{" "}
                    <ChevronRight size={14} className="ml-1 nav-arrow" />
                  </div>
                </div>
              </ReconCardContent>
            </ReconCard>
          ))}
        </div>
      </div>
    );
  }

  // ▼▼ ROOT: Emirate summary view
  return (
    <MaritimeLayout>
      <div className="reconciliation-dashboard">
        {/* Header */}
        <div className="recon-header">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="recon-gradient-title">
                ⚖️ National Maritime Center – Monthly Reconciliation Dashboard
              </h1>
              <p className="text-slate-600 text-lg">
                AI-powered revenue reconciliation and variance analysis
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => exportData("csv")}
                className="recon-btn recon-btn-success"
              >
                <Download className="mr-2" size={18} /> Export CSV
              </button>
              <button
                onClick={() => exportData("pdf")}
                className="recon-btn recon-btn-primary"
              >
                <FileDown className="mr-2" size={18} /> Export PDF
              </button>
            </div>
          </div>

          <p className="text-slate-600 leading-relaxed bg-orange-50 rounded-xl p-4 border border-orange-100">
            This dashboard reconciles the monthly totals from the Revenue
            Metering Engine with bank and PCS remittances. It highlights
            mismatches, provides AI anomaly notes, and allows auditors to
            generate exportable reports with one click.
          </p>
        </div>

        {/* KPI Cards */}
        <div className="recon-kpi-grid">
          <div className="recon-kpi-card">
            <h3 className="recon-kpi-label">Expected (AED)</h3>
            <div className="recon-kpi-value text-slate-800">
              {kpis.expected.toLocaleString()}
            </div>
            <p className="text-sm text-slate-600">
              Sum of metered revenue across all Emirates for {dateRange}.
            </p>
          </div>

          <div className="recon-kpi-card">
            <h3 className="recon-kpi-label">Paid (AED)</h3>
            <div className="recon-kpi-value text-blue-600">
              {kpis.paid.toLocaleString()}
            </div>
            <p className="text-sm text-slate-600">
              Aggregate of bank/PCS settlements received in {dateRange}.
            </p>
          </div>

          <div className="recon-kpi-card">
            <h3 className="recon-kpi-label">Variance (AED)</h3>
            <div
              className={`recon-kpi-value ${getVarianceClass(kpis.variance)}`}
            >
              {kpis.variance > 0 ? "+" : ""}
              {kpis.variance.toLocaleString()}
            </div>
            <p className="text-sm text-slate-600">
              Paid − Expected: negative indicates short remittance; positive
              indicates excess.
            </p>
          </div>

          <div className="recon-kpi-card">
            <h3 className="recon-kpi-label">Match %</h3>
            <div className="recon-kpi-value text-emerald-600">
              {kpis.matchPct.toFixed(2)}%
            </div>
            <p className="text-sm text-slate-600">
              Overall national reconciliation rate for {dateRange}.
            </p>
          </div>
        </div>

        <div className="recon-filter-bar">
          <div className="recon-filter-item">
            <Filter className="mr-2" size={14} /> Filter by Emirate / Port /
            Service / Status
          </div>
          <div className="recon-filter-item">Date Range: {dateRange}</div>
        </div>

        <div className="chart-container">
          <h3 className="recon-card-title mb-4">
            Expected vs Paid by Emirate (AED)
          </h3>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={emirateRecon}>
              <XAxis dataKey="emirate" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Legend />
              <Bar
                dataKey="expected"
                name="Expected"
                fill="#94a3b8"
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="paid"
                name="Paid"
                fill="#1d4ed8"
                radius={[2, 2, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="variance"
                name="Variance"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: "#ef4444", r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-sm text-slate-600 mt-3">
            Click an Emirate column to drill down into its ports. Variance line
            highlights over/under remittance patterns.
          </p>
        </div>

        <div className="emirates-grid">
          {emirateRecon.map((e, i) => (
            <ReconCard
              key={i}
              hover={true}
              onClick={() => setSelectedEmirate(e.emirate)}
              className="emirate-card"
            >
              <ReconCardHeader>
                <ReconCardTitle>{e.emirate}</ReconCardTitle>
              </ReconCardHeader>
              <ReconCardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Expected:</span>
                    <span className="font-semibold">
                      AED {e.expected.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Paid:</span>
                    <span className="font-semibold text-blue-600">
                      AED {e.paid.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Variance:</span>
                    <span
                      className={`font-semibold ${getVarianceClass(
                        e.variance
                      )}`}
                    >
                      {e.variance > 0 ? "+" : ""}
                      {e.variance.toLocaleString()} AED
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Match Rate:</span>
                    <span className="font-semibold text-emerald-600">
                      {e.matchRate}%
                    </span>
                  </div>
                  <div className="flex items-center text-blue-600 font-semibold mt-2">
                    View Ports{" "}
                    <ChevronRight size={14} className="ml-1 nav-arrow" />
                  </div>
                </div>
              </ReconCardContent>
            </ReconCard>
          ))}
        </div>

        <div className="ai-anomalies">
          <h3 className="recon-card-title mb-4">
            <AlertTriangle className="mr-2" size={20} />
            AI Anomaly Highlights
          </h3>
          <ul className="ai-anomaly-list">
            {aiFindings.map((a, idx) => (
              <li key={idx} className="ai-anomaly-item">
                <strong className="text-slate-800">{a.emirate}:</strong>{" "}
                {a.note}
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-600 mt-3 flex items-start">
            <Info className="mr-2 mt-0.5" size={16} />
            These insights are derived from month-over-month variance patterns,
            time-of-posting lags, and PCS vs gateway remittance deltas. Use them
            to prioritize manual review.
          </p>
        </div>
      </div>
    </MaritimeLayout>
  );
}
